import { SIDEBAR_SECTION_KEYS } from "../location-types/constants";

/** Single field definition for a section. canToggle: false = always shown (e.g. title, description for now). */
export interface SectionFieldDef {
  key: string;
  label: string;
  canToggle: boolean;
}

/** All sections and their form fields. Title and description are non-toggleable for now; all others can be shown/hidden. */
export const SECTION_FIELD_DEFINITIONS: Record<string, SectionFieldDef[]> = {
  showNotice: [
    { key: "notice.title", label: "Title", canToggle: false },
    { key: "notice.description", label: "Description", canToggle: false },
    { key: "notice.category", label: "Category", canToggle: true },
    { key: "notice.priority", label: "Priority", canToggle: true },
    { key: "notice.tags", label: "Tags", canToggle: true },
    { key: "notice.images", label: "Images", canToggle: true },
    { key: "notice.visibility", label: "Visibility", canToggle: true },
    { key: "notice.isPinned", label: "Pinned", canToggle: true },
    {
      key: "notice.requiresApproval",
      label: "Requires approval",
      canToggle: true,
    },
    { key: "notice.expiryDate", label: "Expiry date", canToggle: true },
  ],
  showEvent: [
    { key: "event.title", label: "Title", canToggle: false },
    { key: "event.description", label: "Description", canToggle: false },
    { key: "event.eventType", label: "Event type", canToggle: true },
    { key: "event.startTime", label: "Start time", canToggle: true },
    { key: "event.endTime", label: "End time", canToggle: true },
    { key: "event.timezone", label: "Timezone", canToggle: true },
    { key: "event.venueName", label: "Venue name", canToggle: true },
    { key: "event.venueAddress", label: "Venue address", canToggle: true },
    { key: "event.isVirtual", label: "Virtual event", canToggle: true },
    { key: "event.meetingUrl", label: "Meeting URL", canToggle: true },
    { key: "event.coverImage", label: "Cover image", canToggle: true },
    { key: "event.maxAttendees", label: "Max attendees", canToggle: true },
    {
      key: "event.requiresRegistration",
      label: "Requires registration",
      canToggle: true,
    },
    { key: "event.visibility", label: "Visibility", canToggle: true },
  ],
  showTenantBusiness: [
    { key: "tenant.title", label: "Title", canToggle: false },
    { key: "tenant.description", label: "Description", canToggle: false },
    { key: "tenant.category", label: "Category", canToggle: true },
    { key: "tenant.contactInfo", label: "Contact info", canToggle: true },
    { key: "tenant.visibility", label: "Visibility", canToggle: true },
  ],
  showRetail: [
    { key: "retail.title", label: "Title", canToggle: false },
    { key: "retail.description", label: "Description", canToggle: false },
    { key: "retail.price", label: "Price", canToggle: true },
    { key: "retail.category", label: "Category", canToggle: true },
    { key: "retail.contactInfo", label: "Contact info", canToggle: true },
    { key: "retail.visibility", label: "Visibility", canToggle: true },
  ],
  showFoodCourt: [
    { key: "foodCourt.title", label: "Title", canToggle: false },
    { key: "foodCourt.description", label: "Description", canToggle: false },
    { key: "foodCourt.price", label: "Price", canToggle: true },
    { key: "foodCourt.category", label: "Category", canToggle: true },
    { key: "foodCourt.contactInfo", label: "Contact info", canToggle: true },
    { key: "foodCourt.visibility", label: "Visibility", canToggle: true },
  ],
  showFacility: [
    { key: "facility.title", label: "Title", canToggle: false },
    { key: "facility.description", label: "Description", canToggle: false },
    { key: "facility.contentBody", label: "Content body", canToggle: true },
    { key: "facility.category", label: "Category", canToggle: true },
    { key: "facility.facilityType", label: "Facility type", canToggle: true },
    { key: "facility.tags", label: "Tags", canToggle: true },
    { key: "facility.images", label: "Images", canToggle: true },
    { key: "facility.visibility", label: "Visibility", canToggle: true },
    { key: "facility.isPinned", label: "Pinned", canToggle: true },
    {
      key: "facility.requiresApproval",
      label: "Requires approval",
      canToggle: true,
    },
    { key: "facility.expiryDate", label: "Expiry date", canToggle: true },
    { key: "facility.isAvailable", label: "Available", canToggle: true },
    {
      key: "facility.reservationRequired",
      label: "Reservation required",
      canToggle: true,
    },
    {
      key: "facility.operatingHours",
      label: "Operating hours",
      canToggle: true,
    },
    { key: "facility.location", label: "Location", canToggle: true },
    { key: "facility.contact", label: "Contact", canToggle: true },
    { key: "facility.isActive", label: "Active", canToggle: true },
  ],
  showService: [
    { key: "service.title", label: "Title", canToggle: false },
    { key: "service.description", label: "Description", canToggle: false },
    { key: "service.category", label: "Category", canToggle: true },
    { key: "service.contactInfo", label: "Contact info", canToggle: true },
    { key: "service.visibility", label: "Visibility", canToggle: true },
  ],
  showJob: [
    { key: "job.title", label: "Title", canToggle: false },
    { key: "job.description", label: "Description", canToggle: false },
    { key: "job.category", label: "Category", canToggle: true },
    { key: "job.contactInfo", label: "Contact info", canToggle: true },
    { key: "job.visibility", label: "Visibility", canToggle: true },
  ],
  showBuySell: [
    { key: "buySell.title", label: "Title", canToggle: false },
    { key: "buySell.description", label: "Description", canToggle: false },
    { key: "buySell.price", label: "Price", canToggle: true },
    { key: "buySell.category", label: "Category", canToggle: true },
    { key: "buySell.condition", label: "Condition", canToggle: true },
    { key: "buySell.contactInfo", label: "Contact info", canToggle: true },
    { key: "buySell.visibility", label: "Visibility", canToggle: true },
  ],
  showRequestOffer: [
    { key: "requestOffer.title", label: "Title", canToggle: false },
    { key: "requestOffer.description", label: "Description", canToggle: false },
    { key: "requestOffer.category", label: "Category", canToggle: true },
    { key: "requestOffer.contactInfo", label: "Contact info", canToggle: true },
    { key: "requestOffer.visibility", label: "Visibility", canToggle: true },
  ],
  showUrgentHelp: [
    { key: "immediateHelp.title", label: "Title", canToggle: false },
    { key: "immediateHelp.description", label: "Description", canToggle: false },
    { key: "immediateHelp.contactInfo", label: "Contact info", canToggle: true },
  ],
  showAskForHelp: [
    { key: "askForHelp.title", label: "Title", canToggle: false },
    { key: "askForHelp.description", label: "Description", canToggle: false },
    { key: "askForHelp.contactInfo", label: "Contact info", canToggle: true },
  ],
  showShareWithNeighbors: [
    { key: "shareWithNeighbors.title", label: "Title", canToggle: false },
    {
      key: "shareWithNeighbors.description",
      label: "Description",
      canToggle: false,
    },
    {
      key: "shareWithNeighbors.contactInfo",
      label: "Contact info",
      canToggle: true,
    },
  ],
  showLostFound: [
    { key: "lostFound.title", label: "Title", canToggle: false },
    { key: "lostFound.description", label: "Description", canToggle: false },
    { key: "lostFound.type", label: "Type (Lost/Found)", canToggle: true },
    { key: "lostFound.category", label: "Category", canToggle: true },
    {
      key: "lostFound.locationFound",
      label: "Location found",
      canToggle: true,
    },
    { key: "lostFound.dateFound", label: "Date found", canToggle: true },
    { key: "lostFound.contactInfo", label: "Contact info", canToggle: true },
    { key: "lostFound.reward", label: "Reward", canToggle: true },
    { key: "lostFound.visibility", label: "Visibility", canToggle: true },
  ],
  showOtherRequests: [
    { key: "otherRequests.title", label: "Title", canToggle: false },
    {
      key: "otherRequests.description",
      label: "Description",
      canToggle: false,
    },
    {
      key: "otherRequests.contactInfo",
      label: "Contact info",
      canToggle: true,
    },
  ],
};

/** All optional field keys (toggleable only) used in PlatformOptionalFieldVisibility. */
export const OPTIONAL_FIELD_KEYS = (() => {
  const set = new Set<string>();
  for (const sectionKey of SIDEBAR_SECTION_KEYS) {
    const defs = SECTION_FIELD_DEFINITIONS[sectionKey];
    if (defs) for (const d of defs) if (d.canToggle) set.add(d.key);
  }
  return Array.from(set) as readonly string[];
})();

export type OptionalFieldKey = (typeof OPTIONAL_FIELD_KEYS)[number];

/** Short label for each optional (toggleable) field. Used in admin and in section field page. */
export const OPTIONAL_FIELD_LABELS: Record<string, string> = (() => {
  const out: Record<string, string> = {};
  for (const sectionKey of SIDEBAR_SECTION_KEYS) {
    const defs = SECTION_FIELD_DEFINITIONS[sectionKey];
    if (defs) for (const d of defs) if (d.canToggle) out[d.key] = d.label;
  }
  return out;
})();

/** Legacy: short label for section page (same as OPTIONAL_FIELD_LABELS for toggleable). */
export const FIELD_LABELS: Record<string, string> = {
  ...OPTIONAL_FIELD_LABELS,
};

/** Section key → optional (toggleable) field keys for that section. Used for Fields page and save. */
export const SECTION_OPTIONAL_FIELDS: Record<string, readonly string[]> =
  (() => {
    const out: Record<string, string[]> = {};
    for (const sectionKey of SIDEBAR_SECTION_KEYS) {
      const defs = SECTION_FIELD_DEFINITIONS[sectionKey];
      if (defs)
        out[sectionKey] = defs.filter((d) => d.canToggle).map((d) => d.key);
    }
    return out;
  })();

/** All section keys get a "Fields" link (every section has at least title/description). */
export const SECTIONS_WITH_OPTIONAL_FIELDS = [
  ...SIDEBAR_SECTION_KEYS,
] as string[];

/** Field key for Lost & Found reward. Use with getVisibleOptionalFields().byKey when rendering reward UI. */
export const LOST_FOUND_REWARD_FIELD_KEY = "lostFound.reward";

/** Field key for Lost & Found category. Use with getVisibleOptionalFields().byKey when rendering category UI. */
export const LOST_FOUND_CATEGORY_FIELD_KEY = "lostFound.category";
